import { useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import styles from './Tags.module.css';
import AIEduDialog from '../../components/Dialog/AIEduDialog';
import AIEduInputText from '../../components/Input/AIEduInputText';
import AIEduPassword from '../../components/Input/AIEduPassword';
import AIEduButton from '../../components/Button/AIEduButton';
import { copyToClipboard } from '../../utils/Utils';
import { API } from '../../services';
import {
    showErrorToast,
    showSuccessToast,
} from '../../services/setup/ApiResponseHandler';
import { Context } from '../../context/Context';
import { useAuthenticate } from '../../hooks';
import { TAG_NAME_MAX_LENGTH } from '../../utils/Constants';

export const AddTagPopUp = ({
    setTag,
    tagsetId,
    tagsetCategoryId,
    isAddTagPopupVisible,
    setIsAddTagPopupVisible,
    setTagCreatedPopupVisible,
}) => {
    const intl = useIntl();
    const { formatMessage: f } = intl;

    const { accountInfo } = useAuthenticate();
    const { organizations, place } = accountInfo ?? {};
    const { placeId } = place ?? {};
    const { organizationId } = organizations?.[0] ?? {};

    const [tagName, setTagName] = useState('');
    const [isCreatingTag, setIsCreatingTag] = useState(false);

    const createTag = async () => {
        if (isCreatingTag) return;

        setIsCreatingTag(true);
        try {
            const payload = {
                tagName,
                tagsetId,
                tagsetCategoryId,
            };
            const resp = await API.addTag(organizationId, placeId, payload);
            if (resp.status === 201) {
                setTag((prev) => ({
                    ...prev,
                    webHookUrl: resp.data?.data?.webHookUrl,
                    password: resp.data?.data?.password,
                }));
                setTagCreatedPopupVisible(true);
                closePopUp();
            } else {
                showErrorToast(resp?.data?.error || 'Error creating tag');
            }
        } finally {
            setIsCreatingTag(false);
        }
    };

    const closePopUp = () => {
        if (!isAddTagPopupVisible) return;
        setIsAddTagPopupVisible(false);
        setTagName('');
    };

    return (
        <AIEduDialog
            visible={isAddTagPopupVisible}
            draggable={false}
            style={{ width: '30vw', minWidth: '35rem' }}
            content={
                <>
                    <div className={styles.popUpBtnContainer}>
                        Enter the name of tag to create a new tag for device
                        management:
                    </div>
                    <div className='mt-4'>
                        <AIEduInputText
                            title='Tag Name'
                            width={'98%'}
                            placeholder='Enter Tag Name'
                            value={tagName}
                            onChange={(e) => setTagName(e.target.value)}
                            maxLength={TAG_NAME_MAX_LENGTH}
                        />
                    </div>
                </>
            }
            footer={
                <div className='flex justify-content-end'>
                    <AIEduButton
                        label={f({ id: 'AI_EDU_CANCEL' })}
                        className='btnSecondary'
                        onClick={closePopUp}
                    />
                    <AIEduButton
                        label={f({ id: 'AI_EDU_OK' })}
                        className='btnPrimary'
                        onClick={createTag}
                        disabled={tagName.trim().length === 0 || isCreatingTag}
                    />
                </div>
            }
        />
    );
};

export const TagCreatedPopUp = ({
    tag,
    tagCreatedPopupVisible,
    setTagCreatedPopupVisible,
}) => {
    const intl = useIntl();
    const { formatMessage: f } = intl;
    const { triggerRefresh } = useContext(Context);

    const closePopUp = () => {
        if (!tagCreatedPopupVisible) return;
        setTagCreatedPopupVisible(false);
        triggerRefresh('tag');
    };

    const copyWebhookUrl = () => {
        copyToClipboard(tag?.webHookUrl);
        showSuccessToast('Copied');
    };

    const copyApiKey = () => {
        copyToClipboard(tag?.password);
        showSuccessToast('Copied');
    };

    return (
        <AIEduDialog
            visible={tagCreatedPopupVisible}
            draggable={false}
            style={{ width: '30vw', minWidth: '45rem' }}
            content={
                <>
                    <div className={styles.popUpBtnContainer}>
                        To configure API connector, you will need below
                        information:
                    </div>
                    <div className='mt-4'>
                        <div className={`px-4 pt-1 pb-4 ${styles.tagInfo}`}>
                            <div className='flex align-items-end justify-content-between'>
                                <AIEduInputText
                                    title='WebHook URL:'
                                    value={tag?.webHookUrl}
                                    disabled
                                    width={'90%'}
                                />

                                <i
                                    className='pi pi-clone cursor-pointer'
                                    style={{ fontSize: '15px' }}
                                    onClick={copyWebhookUrl}
                                />
                            </div>

                            <div className='flex align-items-end justify-content-between'>
                                <AIEduPassword
                                    title='API Key:'
                                    value={tag?.password}
                                    toggleMask
                                    feedback={false}
                                    disabled
                                    width={'90%'}
                                />

                                <i
                                    className='pi pi-clone cursor-pointer'
                                    style={{ fontSize: '15px' }}
                                    onClick={copyApiKey}
                                />
                            </div>
                        </div>
                    </div>
                    <div
                        className='mt-4'
                        style={{ fontFamily: 'SamsungOne-600' }}
                    >
                        Instructions: <br />
                        You can quickly access the third-party platform via the
                        link below: <br />
                        InformaCast:{' '}
                        <a
                            href='https://admin.icmobile.singlewire.com/icmobile'
                            target='_blank'
                        >
                            https://admin.icmobile.singlewire.com/icmobile
                        </a>
                    </div>
                </>
            }
            footer={
                <div className='flex justify-content-center'>
                    <AIEduButton
                        label={f({ id: 'AI_EDU_DONE' })}
                        className='btnPrimary'
                        style={{ width: '5rem' }}
                        onClick={closePopUp}
                    />
                </div>
            }
        />
    );
};
